import type { VoltrShipping } from '@/types/voltr';

export const EMPTY_SHIPPING: VoltrShipping = {
  name: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
  phone: '',
};

export type ShippingErrors = Partial<Record<keyof VoltrShipping, string>>;

/**
 * What's missing from a ship-to address before it's worth asking the user to
 * confirm it. Shape only (present, a 2-letter state, a 5-digit ZIP): whether
 * the address is real is the brand's and the carrier's call.
 */
export function shippingErrors(shipping: VoltrShipping): ShippingErrors {
  const errors: ShippingErrors = {};
  if (!shipping.name.trim()) errors.name = 'Enter your full name.';
  if (!shipping.line1.trim()) errors.line1 = 'Enter your street address.';
  if (!shipping.city.trim()) errors.city = 'Enter your city.';
  if (!/^[A-Za-z]{2}$/.test(shipping.state.trim())) errors.state = 'Use the 2-letter state, like TX.';
  if (!/^\d{5}(-\d{4})?$/.test(shipping.zip.trim())) errors.zip = 'Enter a 5-digit ZIP.';
  return errors;
}

/** The address with stray spaces removed and the state uppercased, as it's sent. */
export function tidyShipping(shipping: VoltrShipping): VoltrShipping {
  return {
    name: shipping.name.trim(),
    line1: shipping.line1.trim(),
    line2: shipping.line2.trim(),
    city: shipping.city.trim(),
    state: shipping.state.trim().toUpperCase(),
    zip: shipping.zip.trim(),
    country: shipping.country,
    phone: shipping.phone.trim(),
  };
}

/** The address as lines to read back: name, street (with apt), "City, ST ZIP". */
export function shippingLines(shipping: VoltrShipping): string[] {
  return [shipping.name, ...addressLines(shipping)].filter((line) => line.trim().length > 0);
}

/** Where it goes, without the name (Figma "Order Placed": "123 Main St," / "New York, NY 10012"). */
export function addressLines(shipping: VoltrShipping): string[] {
  const street = [shipping.line1, shipping.line2].filter(Boolean).join(', ');
  return [street, `${shipping.city}, ${shipping.state} ${shipping.zip}`].filter((line) => line.trim().length > 0);
}
